"use client";

import { motion } from "motion/react";
import Image from "next/image";
import { ImpactItem } from "./ImpactItem";
import { UsersCard } from "./UsersCard";
import { entrepreneurOutcomes, sdgImages } from "../data";
import {
  slideUpVariants,
  fadeInVariants,
  createStaggerContainer,
} from "@/lib/animations";

const impactStatsContainer = createStaggerContainer(0.3, 0.2);
const sdgContainer = createStaggerContainer(0.1, 0.1);

export function Impact() {
  return (
    <section id="impact">
      <div className="flex flex-col lg:flex-row">
        <div className="bg-green-900 px-5 py-10 md:basis-5/12 md:px-6 lg:px-10 lg:py-20 2xl:basis-1/2">
          <div className="mx-auto max-w-xl 2xl:ml-auto 2xl:mr-0">
            <div className="mb-8 sm:mb-12 md:mb-14">
              <motion.h2
                className="mb-2 text-2xl font-medium tracking-[-0.01em] text-black-400 md:text-3xl lg:mb-4 lg:text-4xl xl:text-5xl"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={slideUpVariants}
              >
                Our Impact
              </motion.h2>
              <motion.p
                className="max-w-lg"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={slideUpVariants}
              >
                We believe that access to productive assets shouldn&apos;t
                depend on access to traditional credit. Every asset we finance
                helps unlock income, create jobs, and build financial resilience
                across underserved communities.
              </motion.p>
            </div>

            <motion.div
              className="space-y-12"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={impactStatsContainer}
            >
              <motion.div
                className="grid grid-cols-2 gap-10 border-b border-dashed border-grey-100 pb-6"
                variants={slideUpVariants}
              >
                <ImpactItem value="4,000+" description="Lives Empowered" />
                <ImpactItem
                  value="35–45%"
                  description="Average Household Income Growth"
                />
              </motion.div>

              <motion.div
                className="grid grid-cols-2 gap-10 border-b border-dashed border-grey-100 pb-6"
                variants={slideUpVariants}
              >
                <ImpactItem
                  value="300+"
                  description="Income Opportunities Created"
                />
                <ImpactItem
                  value="100%"
                  description="Focused on Financial Inclusion"
                />
              </motion.div>

              <motion.div
                className="grid grid-cols-4 gap-2 lg:flex lg:justify-between"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sdgContainer}
              >
                {sdgImages.map((image) => (
                  <motion.div key={image.id} variants={fadeInVariants}>
                    <Image
                      src={image.src}
                      alt={`${image.alt} image`}
                      width={68}
                      height={68}
                      className="w-full object-contain"
                    />
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                className="border-t border-dashed border-grey-100 pt-6 text-black-100"
                variants={slideUpVariants}
              >
                <p className="text-lg font-medium leading-7 tracking-copy">
                  By removing barriers to ownership, Pluck is helping
                  entrepreneurs:
                </p>
                <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-6 marker:text-green-1000">
                  {entrepreneurOutcomes.map((outcome) => (
                    <li key={outcome} className="pl-1">
                      {outcome}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="bg-white p-5 md:flex-1 md:p-6 lg:p-10 2xl:basis-1/2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInVariants}
        >
          <div className="mx-auto flex h-full max-w-xl items-center justify-center 2xl:mx-0">
            <UsersCard />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
